import React from "react";
import * as Switch from "@radix-ui/react-switch";
import { useState } from "react";
import emailjs from "@emailjs/browser";
import { CrossIcon } from "../../icons/cross";

const CalculationContent = {
  heading: "Unlock the True Potential of Your Project",
  para: "Select the key elements you need, and explore how our expertise can bring your vision to life with precision and innovation.",
};

const sections = [
  {
    title: "Design",
    description:
      "Crafting visually stunning products that align with your objectives and engage users.",
    services: [
      {
        name: "Landing Page",
        description:
          "Designing compelling landing pages tailored to your audience.",
      },
      {
        name: "Corporate",
        description:
          "Building professional corporate websites that showcase your brand.",
      },
      {
        name: "Online Store",
        description:
          "Developing e-commerce platforms to maximize your online sales.",
      },
      {
        name: "Portal",
        description:
          "Creating portals for seamless data access and user interaction.",
      },
      {
        name: "Confidential Projects",
        description:
          "Executing unique projects under NDA with utmost discretion.",
      },
      {
        name: "Consultation",
        description:
          "Offering expert advice for new or evolving project ideas.",
      },
    ],
  },
  {
    title: "Website Development",
    description:
      "Enhancing your digital presence with precision and strategic execution.",
    services: [
      {
        name: "Custom Website Development",
        description: "Building websites tailored to your business needs.",
      },
      {
        name: "Responsive Web Design",
        description: "Optimizing websites for seamless multi-device viewing.",
      },
      {
        name: "E-commerce Solutions",
        description: "Creating secure platforms for online transactions.",
      },
      {
        name: "User Experience (UX) Optimization",
        description: "Improving usability for a frictionless user journey.",
      },
      {
        name: "Content Management Systems (CMS)",
        description: "Developing solutions for effortless content updates.",
      },
      {
        name: "Mobile App Integration",
        description: "Connecting websites with mobile applications seamlessly.",
      },
      {
        name: "SEO-friendly Web Development",
        description: "Structuring sites for improved search engine rankings.",
      },
      {
        name: "Performance Optimization",
        description: "Enhancing speed and efficiency for better performance.",
      },
    ],
  },
  {
    title: "AI Solutions",
    description:
      "Leveraging artificial intelligence to automate workflows and enhance digital experiences.",
    services: [
      {
        name: "AI Bot Development",
        description:
          "Creating intelligent chatbots and virtual assistants to improve customer interactions.",
      },
      {
        name: "AI Workflow Automations",
        description:
          "Implementing AI-driven solutions to streamline and optimize business operations.",
      },
      {
        name: "AI Data Analysis",
        description:
          "Utilizing machine learning to extract insights from large datasets.",
      },
      {
        name: "AI-Powered Recommendation Systems",
        description:
          "Developing personalized recommendation engines for enhanced user experience.",
      },
      {
        name: "AI Image & Video Processing",
        description:
          "Applying AI for image recognition, video analysis, and media enhancement.",
      },
      {
        name: "AI Text & Speech Processing",
        description:
          "Creating AI models for natural language processing and speech recognition.",
      },
      {
        name: "AI Predictive Analytics",
        description:
          "Using AI to forecast trends and improve decision-making processes.",
      },
      {
        name: "AI Cybersecurity Solutions",
        description:
          "Enhancing security through AI-driven threat detection and prevention.",
      },
    ],
  },
  {
    title: "Cybersecurity",
    description:
      "Protecting digital assets with advanced security measures and threat intelligence.",
    services: [
      {
        name: "Network Security",
        description:
          "Implementing firewalls, intrusion detection, and secure network architectures.",
      },
      {
        name: "Cloud Security",
        description:
          "Securing cloud infrastructures and preventing unauthorized access.",
      },
      {
        name: "Threat Intelligence",
        description:
          "Monitoring and analyzing cyber threats to mitigate risks proactively.",
      },
      {
        name: "Penetration Testing",
        description:
          "Conducting ethical hacking to identify and fix security vulnerabilities.",
      },
      {
        name: "Data Protection & Encryption",
        description:
          "Ensuring sensitive data remains secure through encryption techniques.",
      },
      {
        name: "Security Compliance & Audits",
        description:
          "Helping businesses meet cybersecurity compliance standards and regulations.",
      },
    ],
  },
  {
    title: "Graphic Design",
    description:
      "Shaping visuals that create lasting impressions and emotional connections.",
    services: [
      {
        name: "Advertising",
        description: "Designing engaging ads to promote your business.",
      },
      {
        name: "Marketing Collateral",
        description: "Creating assets to boost your marketing campaigns.",
      },
      {
        name: "Imagery",
        description:
          "Producing visuals that effectively communicate your brand.",
      },
      {
        name: "Photorealistic Content",
        description: "Crafting high-quality, lifelike visual elements.",
      },
      {
        name: "Print Design",
        description:
          "Designing brochures, flyers, and other printed materials.",
      },
      {
        name: "Character Creation",
        description: "Illustrating custom characters to represent your brand.",
      },
      {
        name: "Animation",
        description: "Bringing ideas to life through motion graphics.",
      },
    ],
  },
  {
    title: "CMS Development",
    description: "Delivering seamless content management and user engagement.",
    services: [
      {
        name: "CMS Excellence",
        description: "Providing robust content management solutions.",
      },
      {
        name: "Tailored CMS Solutions",
        description: "Customizing CMS platforms to match your needs.",
      },
      {
        name: "Dynamic Content",
        description: "Creating flexible and engaging digital experiences.",
      },
      {
        name: "User-Friendly Interfaces",
        description: "Designing intuitive CMS layouts for efficiency.",
      },
      {
        name: "Content Organization",
        description: "Structuring information for optimal usability.",
      },
      {
        name: "Narrative Web Design",
        description: "Integrating storytelling into web experiences.",
      },
      {
        name: "CMS Integration",
        description: "Seamlessly linking CMS with existing infrastructure.",
      },
      {
        name: "Storytelling Websites",
        description: "Developing platforms that narrate brand journeys.",
      },
      {
        name: "Digital Content Management",
        description: "Optimizing strategies for handling digital assets.",
      },
      {
        name: "CMS Customization",
        description: "Personalizing CMS to reflect your brand’s identity.",
      },
    ],
  },
];

const Calculation: React.FC = () => {
  const { para, heading } = CalculationContent;
  const [showSections, setShowSections] = useState<boolean[]>(
    Array(sections.length).fill(false)
  );
  const [showModal, setShowModal] = useState(false);
  const [selectedServices, setSelectedServices] = useState<
    { title: string; services: string[] }[]
  >([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });

  function handleToggle(index: number) {
    setShowSections((prev) => {
      const newShowSections = [...prev];
      newShowSections[index] = !newShowSections[index];
      return newShowSections;
    });
  }
  function handleToggleModal() {
    setShowModal(!showModal);
  }

  const isSelected = (title: string, serviceName: string) =>
    selectedServices.some(
      (s) => s.title === title && s.services.includes(serviceName)
    );

  function handleServiceSelection(
    title: string,
    service: string,
    isSelected: boolean
  ) {
    setSelectedServices((prev) => {
      const updatedServices = prev.map((section) =>
        section.title === title
          ? {
              ...section,
              services: isSelected
                ? section.services.filter((s) => s !== service)
                : [...section.services, service],
            }
          : section
      );

      if (!updatedServices.some((s) => s.title === title) && !isSelected) {
        updatedServices.push({ title, services: [service] });
      }
      return updatedServices.filter((s) => s.services.length > 0);
    });
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();

    const templateParams = {
      user_name: formData.name,
      user_email: formData.email,
      user_phone: formData.phone,
      user_company: formData.company,
      selected_services: selectedServices
        .map((section) => `${section.title}: ${section.services.join(", ")}`)
        .join("\n"),
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAIL_SERVICE_KEY || "",
        process.env.REACT_APP_SERVICE_QUERY_TEMPLATE_ID || "",
        templateParams,
        process.env.REACT_APP_EMAIL_PUBLIC_KEY_ID || ""
      )
      .then(
        (response) => {
          console.log(
            "Email sent successfully!",
            response.status,
            response.text
          );
          alert("Your request has been sent successfully!");
        },
        (error) => {
          console.error("Email failed to send!", error);
          alert("Failed to send your request. Please try again.");
        }
      );
  };

  return (
    <div
      className="py-[2em] sm:py-[3.25em] md:py-[4em] xl:py-[6.25em] story relative border-t border-solid border-[#181823]"
      id="calculation"
    >
      <div className="w-full mx-auto max-w-[94.25em] px-[1.5em] md:px-[2.5em]">
        <div className="w-full mb-6">
          <h2 className="text-white text-2xl md:text-[38px] leading-[1.25em] font-light mb-5">
            {heading}
          </h2>
          <p className="text-white text-[1.2em] md:text-[2em] leading-[1.4em] font-light mb-0">
            {para}
          </p>
        </div>
        <div className="flex flex-col">
          {sections.map((section, index) => (
            <div key={index} className="py-8 xl:py-12">
              <div className="flex justify-between">
                <div className="flex flex-col gap-5">
                  <h3 className="text-white text-[2em] md:text-4xl leading-[1.2em] font-medium mb-0">
                    {section.title}
                  </h3>
                  <p className="text-white text-[1em] md:text-xl leading-[1.4em]">
                    {section.description}
                  </p>
                </div>
                <div className="inline-flex">
                  <Switch.Root
                    className="bg-[#162239f2] w-[66px] md:w-[80px] h-8 md:h-[40px] rounded-full relative"
                    onClick={() => handleToggle(index)}
                  >
                    <Switch.Thumb className="block w-[26px] md:w-8 h-[26px] md:h-8 bg-white rounded-full transition-transform transform translate-x-1 data-[state=checked]:translate-x-[35px] md:data-[state=checked]:translate-x-[42px]" />
                  </Switch.Root>
                </div>
              </div>
              <div
                className={`flex mt-10 relative ${
                  showSections[index] ? "!flex" : "hidden"
                }`}
                style={{ display: "none" }}
              >
                <div className="absolute top-0 left-0 h-full w-6 sm:w-10 bg-gradient-to-r from-[#0A0A10] to-transparent pointer-events-none z-[2]" />
                <div className="absolute top-0 right-0 h-full w-6 sm:w-10 bg-gradient-to-l from-[#0A0A10] to-transparent pointer-events-none z-[2]" />
                <div className="flex gap-3 overflow-x-auto w-full">
                  {section.services.map((service, idx) => {
                    const selected = isSelected(section.title, service.name);

                    return (
                      <div
                        key={idx}
                        className={`flex flex-col gap-2 shrink-0 px-6 py-6 w-[180px] sm:w-[220px] md:w-[260px] rounded-xl border border-solid transition-all duration-300 backdrop-blur-lg shadow-md cursor-pointer ${
                          selected
                            ? "bg-[#323738] border-[rgba(58,231,248,0.59)] text-white shadow-lg"
                            : "bg-[#a5a5a5] border-[#ffffff33] text-gray-300 hover:border-[rgba(58,231,248,0.59) hover:text-white"
                        }`}
                        onClick={() =>
                          handleServiceSelection(
                            section.title,
                            service.name,
                            selected
                          )
                        }
                      >
                        <h5
                          className={`text-[1em] font-medium pr-[16px] mb-0 ${
                            selected ? "text-white" : "text-black"
                          }`}
                        >
                          {service.name}
                        </h5>
                        <p
                          className={`text-[0.875em] font-medium opacity-50 ${
                            selected ? "text-gray-300" : "text-[#0E0E13]"
                          }`}
                        >
                          {service.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Calculation */}
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-5 border-t border-t-[#525258] py-8 xl:py-12">
          <div className="flex flex-col gap-3">
            <p className="text-white text-[16px] xl:text-[20px] leading-[1.5] font-medium">
              {/* Development cost (avg.) */}
            </p>
          </div>
          <div>
            <button
              onClick={handleToggleModal}
              className="py-3 md:py-4 px-5 md:px-6 bg-white rounded-[5.56em] text-[#0E0E13] text-base leading-[1.2] font-medium transition-all duration-300 border border-[#ffffff] hover:text-[white] hover:bg-[#0E0E13] w-full sm:w-auto"
            >
              Discuss the project
            </button>
            <div
              className={`items-center flex-col justify-center backdrop-blur-[10px] fixed inset-0 z-10 h-svh p-5 ${
                showModal ? "!flex" : ""
              }`}
              style={{ display: "none" }}
            >
              <div className="flex flex-col overflow-y-auto py-6 px-5 sm:p-8 rounded-2xl bg-[#162239f2] w-full max-w-[640px] relative">
                <span
                  className="w-6 h-6 absolute top-3 right-3 cursor-pointer flex items-center justify-center"
                  onClick={handleToggleModal}
                >
                  <CrossIcon fill="white" className="w-5 h-5" />
                </span>
                <div className="w-full">
                  <h3 className="text-xl md:text-2xl font-light text-center text-white mb-6">
                    Let’s Discuss Your Project
                  </h3>
                  {selectedServices.length ? (
                    <div className="bg-[#181823] p-5 rounded-lg text-white mb-6">
                      {selectedServices.map((section, i) => (
                        <div key={i} className="mb-4">
                          <h4 className="text-lg font-semibold mb-2 border-b border-gray-600 pb-2">
                            {section.title}
                          </h4>
                          <ul className="space-y-1 text-sm">
                            {section.services.map((service, j) => (
                              <li key={j} className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-[#3A6FF8] rounded-full"></span>{" "}
                                {service}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-white text-center mb-4">
                      No services selected.
                    </p>
                  )}
                  {/* <div className="text-base md:text-xl leading-[1.4] font-light text-white text-center mb-8 md:mb-10">
                    Total Cost (avg.): USD <span>0</span>
                  </div> */}
                  <form className="w-full" onSubmit={sendEmail}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 md:mb-6">
                      <div className="w-full relative">
                        <input
                          type="text"
                          placeholder="Your Full Name"
                          onChange={handleInputChange}
                          required
                          className="w-full text-white text-sm font-normal rounded-xl py-[14px] px-5 bg-[#181823] focus:outline-none focus:shadow-none"
                        />
                      </div>
                      <div className="w-full relative">
                        <input
                          type="email"
                          placeholder="Email"
                          onChange={handleInputChange}
                          required
                          className="w-full text-white text-sm font-normal rounded-xl py-[14px] px-5 bg-[#181823] focus:outline-none focus:shadow-none"
                        />
                      </div>
                      <div className="w-full relative">
                        <input
                          type="text"
                          placeholder="Phone"
                          onChange={handleInputChange}
                          required
                          className="w-full text-white text-sm font-normal rounded-xl py-[14px] px-5 bg-[#181823] focus:outline-none focus:shadow-none"
                        />
                      </div>
                      <div className="w-full relative">
                        <input
                          type="text"
                          placeholder="Company Name / VAT"
                          onChange={handleInputChange}
                          required
                          className="w-full text-white text-sm font-normal rounded-xl py-[14px] px-5 bg-[#181823] focus:outline-none focus:shadow-none"
                        />
                      </div>
                    </div>
                    <div className="text-center">
                      <button className="py-[14px] px-8 md:px-10 bg-[#181823] rounded-[5.56em] text-white text-sm leading-[1.4] font-medium transition-all duration-300 border-none hover:text-[white] hover:bg-[#0E0E13] w-full sm:w-auto sm:min-w-[120px]">
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculation;
